import { IOnboardingRepository } from '../../domain/repositories/IOnboardingRepository';
import { OnboardingProgress, OnboardingProgressProps } from '../../domain/entities/OnboardingProgress';
import { OnboardingStatusValues, isValidOnboardingStatus } from '../../domain/value-objects/OnboardingStatus';
import { supabase } from '@shared/config/supabase';

/**
 * Database row interface for onboarding_progress table
 */
interface OnboardingProgressRow {
  id: number;
  user_id: string;
  role: string;
  status: string;
  current_step_index: number;
  started_at: string | null;
  completed_at: string | null;
  skipped_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Supabase Onboarding Repository
 * Implements IOnboardingRepository using Supabase as the backend
 *
 * Database Schema Expected:
 * Table: onboarding_progress
 * - id (bigint, PK)
 * - user_id (uuid, FK -> auth.users)
 * - role (text: director, teacher, student)
 * - status (text: pending, in_progress, completed, skipped)
 * - current_step_index (integer)
 * - started_at (timestamptz, nullable)
 * - completed_at (timestamptz, nullable)
 * - skipped_at (timestamptz, nullable)
 * - created_at (timestamptz)
 * - updated_at (timestamptz)
 *
 * Table: onboarding_step_completions
 * - id (bigint, PK)
 * - user_id (uuid, FK -> auth.users)
 * - role (text)
 * - step_id (text)
 * - completed_at (timestamptz)
 *
 * RPC Functions:
 * - get_or_create_onboarding_progress(p_user_id uuid, p_role text)
 * - update_onboarding_progress(p_user_id uuid, p_role text, p_status text, p_current_step_index integer)
 */
export class SupabaseOnboardingRepository implements IOnboardingRepository {
  /**
   * Get total steps for a role by counting steps in configuration
   * This should match the steps defined in onboarding.store.ts
   */
  private getTotalStepsForRole(role: string): number {
    // Step counts per role (matches onboarding.store.ts)
    const stepCounts: Record<string, number> = {
      director: 5,
      teacher: 5,
      student: 4,
    };
    return stepCounts[role] ?? 5;
  }

  /**
   * Convert database row to OnboardingProgress entity
   */
  private rowToEntity(row: OnboardingProgressRow): OnboardingProgress {
    if (!isValidOnboardingStatus(row.status)) {
      throw new Error(`Invalid onboarding status: ${row.status}`);
    }

    const props: OnboardingProgressProps = {
      id: String(row.id),
      userId: row.user_id,
      role: row.role as 'director' | 'teacher' | 'student',
      status: row.status,
      currentStepIndex: row.current_step_index,
      totalSteps: this.getTotalStepsForRole(row.role),
      startedAt: row.started_at ? new Date(row.started_at) : null,
      completedAt: row.completed_at ? new Date(row.completed_at) : null,
      skippedAt: row.skipped_at ? new Date(row.skipped_at) : null,
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at),
    };

    return new OnboardingProgress(props);
  }

  /**
   * Get onboarding progress for a user and role.
   * Creates a new record with 'pending' status if none exists.
   * Falls back to localStorage if Supabase is not available.
   */
  async getProgress(userId: string, role: string): Promise<OnboardingProgress> {
    try {
      // Use RPC function to get or create progress
      const { data, error } = await supabase.rpc('get_or_create_onboarding_progress', {
        p_user_id: userId,
        p_role: role,
      });

      if (error) {
        console.warn('Supabase onboarding not available, using localStorage fallback:', error.message);
        return this.getProgressFromLocalStorage(userId, role);
      }

      if (!data) {
        console.warn('No data from Supabase, using localStorage fallback');
        return this.getProgressFromLocalStorage(userId, role);
      }

      // Sync to localStorage as backup
      this.saveProgressToLocalStorage(userId, role, data as OnboardingProgressRow);
      return this.rowToEntity(data as OnboardingProgressRow);
    } catch (error) {
      console.warn('Error in getProgress, using localStorage fallback:', error);
      return this.getProgressFromLocalStorage(userId, role);
    }
  }

  /**
   * Get progress from localStorage (fallback when Supabase unavailable)
   */
  private getProgressFromLocalStorage(userId: string, role: string): OnboardingProgress {
    const key = `onboarding_progress_${userId}_${role}`;
    const stored = localStorage.getItem(key);

    if (stored) {
      try {
        const data = JSON.parse(stored);
        return this.rowToEntity(data as OnboardingProgressRow);
      } catch {
        // Invalid stored data, create new
      }
    }

    // Create new pending progress
    const newProgress: OnboardingProgressRow = {
      id: Date.now(),
      user_id: userId,
      role: role,
      status: 'pending',
      current_step_index: 0,
      started_at: null,
      completed_at: null,
      skipped_at: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    localStorage.setItem(key, JSON.stringify(newProgress));
    return this.rowToEntity(newProgress);
  }

  /**
   * Save progress to localStorage (backup)
   */
  private saveProgressToLocalStorage(userId: string, role: string, data: OnboardingProgressRow): void {
    const key = `onboarding_progress_${userId}_${role}`;
    localStorage.setItem(key, JSON.stringify(data));
  }

  /**
   * Update progress in localStorage (fallback)
   */
  private updateProgressInLocalStorage(
    userId: string,
    role: string,
    updates: Partial<OnboardingProgressRow>
  ): OnboardingProgressRow {
    const key = `onboarding_progress_${userId}_${role}`;
    const stored = localStorage.getItem(key);

    let current: OnboardingProgressRow;
    if (stored) {
      current = JSON.parse(stored);
    } else {
      current = {
        id: Date.now(),
        user_id: userId,
        role: role,
        status: 'pending',
        current_step_index: 0,
        started_at: null,
        completed_at: null,
        skipped_at: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
    }

    const updated: OnboardingProgressRow = {
      ...current,
      ...updates,
      updated_at: new Date().toISOString(),
    };

    localStorage.setItem(key, JSON.stringify(updated));
    return updated;
  }

  /**
   * Start the onboarding process.
   * Sets status to 'in_progress' and records startedAt timestamp.
   */
  async startOnboarding(userId: string, role: string): Promise<OnboardingProgress> {
    try {
      // Use RPC function to update progress
      const { data, error } = await supabase.rpc('update_onboarding_progress', {
        p_user_id: userId,
        p_role: role,
        p_status: OnboardingStatusValues.IN_PROGRESS,
        p_current_step_index: 0,
      });

      if (error) {
        console.warn('Supabase unavailable for startOnboarding, using localStorage:', error.message);
        const updated = this.updateProgressInLocalStorage(userId, role, {
          status: OnboardingStatusValues.IN_PROGRESS,
          current_step_index: 0,
          started_at: new Date().toISOString(),
        });
        return this.rowToEntity(updated);
      }

      if (!data) {
        const updated = this.updateProgressInLocalStorage(userId, role, {
          status: OnboardingStatusValues.IN_PROGRESS,
          current_step_index: 0,
          started_at: new Date().toISOString(),
        });
        return this.rowToEntity(updated);
      }

      this.saveProgressToLocalStorage(userId, role, data as OnboardingProgressRow);
      return this.rowToEntity(data as OnboardingProgressRow);
    } catch (error) {
      console.warn('Error in startOnboarding, using localStorage:', error);
      const updated = this.updateProgressInLocalStorage(userId, role, {
        status: OnboardingStatusValues.IN_PROGRESS,
        current_step_index: 0,
        started_at: new Date().toISOString(),
      });
      return this.rowToEntity(updated);
    }
  }

  /**
   * Update the current step index.
   * Automatically sets status to 'completed' if step exceeds total.
   */
  async updateStep(userId: string, role: string, stepIndex: number): Promise<OnboardingProgress> {
    const totalSteps = this.getTotalStepsForRole(role);
    const status = stepIndex >= totalSteps
      ? OnboardingStatusValues.COMPLETED
      : OnboardingStatusValues.IN_PROGRESS;

    try {
      // Use RPC function to update progress
      const { data, error } = await supabase.rpc('update_onboarding_progress', {
        p_user_id: userId,
        p_role: role,
        p_status: status,
        p_current_step_index: stepIndex,
      });

      if (error) {
        console.warn('Supabase unavailable for updateStep, using localStorage:', error.message);
        const updates: Partial<OnboardingProgressRow> = {
          status: status,
          current_step_index: stepIndex,
        };
        if (status === OnboardingStatusValues.COMPLETED) {
          updates.completed_at = new Date().toISOString();
        }
        const updated = this.updateProgressInLocalStorage(userId, role, updates);
        return this.rowToEntity(updated);
      }

      if (!data) {
        const updates: Partial<OnboardingProgressRow> = {
          status: status,
          current_step_index: stepIndex,
        };
        if (status === OnboardingStatusValues.COMPLETED) {
          updates.completed_at = new Date().toISOString();
        }
        const updated = this.updateProgressInLocalStorage(userId, role, updates);
        return this.rowToEntity(updated);
      }

      this.saveProgressToLocalStorage(userId, role, data as OnboardingProgressRow);
      return this.rowToEntity(data as OnboardingProgressRow);
    } catch (error) {
      console.warn('Error in updateStep, using localStorage:', error);
      const updates: Partial<OnboardingProgressRow> = {
        status: status,
        current_step_index: stepIndex,
      };
      if (status === OnboardingStatusValues.COMPLETED) {
        updates.completed_at = new Date().toISOString();
      }
      const updated = this.updateProgressInLocalStorage(userId, role, updates);
      return this.rowToEntity(updated);
    }
  }

  /**
   * Record completion of a specific step (for analytics).
   * Does not throw on error - analytics should not block UX.
   */
  async completeStep(userId: string, role: string, stepId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('onboarding_step_completions')
        .insert({
          user_id: userId,
          role: role,
          step_id: stepId,
          completed_at: new Date().toISOString(),
        });

      if (error) {
        // Only log error, don't throw (analytics should not block UX)
        console.warn('Failed to record step completion (non-critical):', error);
      }
    } catch (error) {
      // Silently handle errors for analytics
      console.warn('Error recording step completion:', error);
    }
  }

  /**
   * Complete the entire onboarding.
   * Sets status to 'completed' and records completedAt timestamp.
   */
  async completeOnboarding(userId: string, role: string): Promise<OnboardingProgress> {
    const totalSteps = this.getTotalStepsForRole(role);

    try {
      // Use RPC function to update progress
      const { data, error } = await supabase.rpc('update_onboarding_progress', {
        p_user_id: userId,
        p_role: role,
        p_status: OnboardingStatusValues.COMPLETED,
        p_current_step_index: totalSteps,
      });

      if (error) {
        console.warn('Supabase unavailable for completeOnboarding, using localStorage:', error.message);
        const updated = this.updateProgressInLocalStorage(userId, role, {
          status: OnboardingStatusValues.COMPLETED,
          current_step_index: totalSteps,
          completed_at: new Date().toISOString(),
        });
        return this.rowToEntity(updated);
      }

      if (!data) {
        const updated = this.updateProgressInLocalStorage(userId, role, {
          status: OnboardingStatusValues.COMPLETED,
          current_step_index: totalSteps,
          completed_at: new Date().toISOString(),
        });
        return this.rowToEntity(updated);
      }

      this.saveProgressToLocalStorage(userId, role, data as OnboardingProgressRow);
      return this.rowToEntity(data as OnboardingProgressRow);
    } catch (error) {
      console.warn('Error in completeOnboarding, using localStorage:', error);
      const updated = this.updateProgressInLocalStorage(userId, role, {
        status: OnboardingStatusValues.COMPLETED,
        current_step_index: totalSteps,
        completed_at: new Date().toISOString(),
      });
      return this.rowToEntity(updated);
    }
  }

  /**
   * Skip the onboarding.
   * Sets status to 'skipped' and records skippedAt timestamp.
   */
  async skipOnboarding(userId: string, role: string): Promise<OnboardingProgress> {
    try {
      // Use RPC function to update progress
      const { data, error } = await supabase.rpc('update_onboarding_progress', {
        p_user_id: userId,
        p_role: role,
        p_status: OnboardingStatusValues.SKIPPED,
        p_current_step_index: null, // Keep current index
      });

      if (error) {
        console.warn('Supabase unavailable for skipOnboarding, using localStorage:', error.message);
        const updated = this.updateProgressInLocalStorage(userId, role, {
          status: OnboardingStatusValues.SKIPPED,
          skipped_at: new Date().toISOString(),
        });
        return this.rowToEntity(updated);
      }

      if (!data) {
        const updated = this.updateProgressInLocalStorage(userId, role, {
          status: OnboardingStatusValues.SKIPPED,
          skipped_at: new Date().toISOString(),
        });
        return this.rowToEntity(updated);
      }

      this.saveProgressToLocalStorage(userId, role, data as OnboardingProgressRow);
      return this.rowToEntity(data as OnboardingProgressRow);
    } catch (error) {
      console.warn('Error in skipOnboarding, using localStorage:', error);
      const updated = this.updateProgressInLocalStorage(userId, role, {
        status: OnboardingStatusValues.SKIPPED,
        skipped_at: new Date().toISOString(),
      });
      return this.rowToEntity(updated);
    }
  }

  /**
   * Reset onboarding to initial state.
   * Clears step completions and resets progress to 'pending'.
   */
  async resetOnboarding(userId: string, role: string): Promise<OnboardingProgress> {
    try {
      // Delete all step completions for this user-role
      await supabase
        .from('onboarding_step_completions')
        .delete()
        .eq('user_id', userId)
        .eq('role', role);

      // Reset progress to pending state using direct update
      const { data, error } = await supabase
        .from('onboarding_progress')
        .update({
          status: OnboardingStatusValues.PENDING,
          current_step_index: 0,
          started_at: null,
          completed_at: null,
          skipped_at: null,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userId)
        .eq('role', role)
        .select()
        .single();

      if (error) {
        console.warn('Supabase unavailable for resetOnboarding, using localStorage:', error.message);
        const updated = this.updateProgressInLocalStorage(userId, role, {
          status: OnboardingStatusValues.PENDING,
          current_step_index: 0,
          started_at: null,
          completed_at: null,
          skipped_at: null,
        });
        return this.rowToEntity(updated);
      }

      if (!data) {
        const updated = this.updateProgressInLocalStorage(userId, role, {
          status: OnboardingStatusValues.PENDING,
          current_step_index: 0,
          started_at: null,
          completed_at: null,
          skipped_at: null,
        });
        return this.rowToEntity(updated);
      }

      this.saveProgressToLocalStorage(userId, role, data as OnboardingProgressRow);
      return this.rowToEntity(data as OnboardingProgressRow);
    } catch (error) {
      console.warn('Error in resetOnboarding, using localStorage:', error);
      const updated = this.updateProgressInLocalStorage(userId, role, {
        status: OnboardingStatusValues.PENDING,
        current_step_index: 0,
        started_at: null,
        completed_at: null,
        skipped_at: null,
      });
      return this.rowToEntity(updated);
    }
  }

  /**
   * Get list of completed step IDs for a user-role.
   * Used for analytics and resuming partial progress.
   */
  async getCompletedSteps(userId: string, role: string): Promise<string[]> {
    try {
      const { data, error } = await supabase
        .from('onboarding_step_completions')
        .select('step_id')
        .eq('user_id', userId)
        .eq('role', role)
        .order('completed_at', { ascending: true });

      if (error) {
        throw new Error(`Failed to get completed steps: ${error.message}`);
      }

      return (data ?? []).map((row: { step_id: string }) => row.step_id);
    } catch (error) {
      console.error('Error in getCompletedSteps:', error);
      throw error;
    }
  }
}
